/**
 * El nombre de un equipo, armado desde lo que se eligió.
 *
 * POR QUE ESTE ARCHIVO EXISTE
 * ---------------------------
 * Un teléfono ya no se nombra: se describe. El modelo, la memoria, el color y
 * la batería son cuatro respuestas a cuatro preguntas, y el nombre es lo que
 * sale de ellas. Nadie tiene que escribirlo, y por lo tanto nadie tiene que
 * acordarse de cómo se escribía.
 *
 * Eso ya era así en los dos formularios guiados, pero cada uno tenía SU
 * armador: el alta de sellados escribía "IPHONE 17 PRO 256 GB" y el asistente
 * de usados "IPHONE 17 PRO 256GB (USADO)". Mismo teléfono, dos formatos, y
 * ninguna de las dos funciones sabía de la otra. En la base quedaron las dos
 * formas mezcladas y el buscador las trata como productos distintos.
 *
 * Acá vive el único armador. Los dos formularios y el editor completo lo
 * llaman, y el formato del nombre guardado se cambia en un solo lugar.
 *
 * QUE ENTRA EN EL NOMBRE Y QUE NO
 * -------------------------------
 * Entra lo que NO cambia nunca: marca, modelo y capacidad. Y el "(USADO)", que
 * no es un atributo del aparato sino de qué se está vendiendo.
 *
 * No entran el color ni la batería, aunque los dos se pregunten en el alta.
 * Viven en sus columnas y se le suman al nombre AL MOSTRARLO (ver
 * `catalogo/nombre.ts`). La batería porque se vuelve a medir y baja: un nombre
 * que se mueve no sirve para buscar, y el remito de ayer terminaría diciendo
 * otra cosa que la ficha de hoy. El color porque un color escrito adentro del
 * nombre no lo puede leer ningún filtro ni ninguna columna — es el mismo error
 * que ya se pagó con el "A PEDIDO" de los usados.
 */
import { COLORES_POR_MODELO } from "@/components/catalogo/colores";

import { etiquetaCapacidad, parsearIphone } from "./logica";
import { GENERACIONES, modeloDe, partirModelo, variantesDe } from "./modelos";

/**
 * Con qué marca empieza el nombre GUARDADO de un teléfono.
 *
 * Solo sirve para leer fichas que ya están (`esEquipo`): qué es un teléfono y
 * qué es un cable. Para cargar uno nuevo no decide nada —eso lo decide lo que
 * se eligió, ver `esIphoneElegido`—.
 */
const MARCAS = ["IPHONE", "SAMSUNG", "XIAOMI", "MOTOROLA", "GOOGLE", "GALAXY", "REDMI", "POCO"];

/**
 * Las capacidades reales de los equipos que pasan por el local.
 *
 * Vivia en `usados/logica.ts` y el alta de sellados la importaba de ahi, que es
 * al reves de como se lee: la memoria no es un dato de los usados, es un dato
 * de cualquier telefono.
 */
export const MEMORIAS = [64, 128, 256, 512, 1024] as const;

/**
 * Las marcas que se eligen con un botón en «Otra marca». Son las que pasan por
 * el local; cualquier otra se escribe con «Otra». Con botones no hay
 * «SAMSUMG» ni «Samsung» contra «SAMSUNG»: la marca es un filtro, y un filtro
 * con tres grafías de lo mismo no filtra.
 */
export const MARCAS_DE_CELULAR = ["SAMSUNG", "MOTOROLA", "XIAOMI", "GOOGLE"] as const;

/**
 * SE ELIGIO UN iPHONE DEL LINEUP.
 *
 * Es LA pregunta que decide todo lo demas —si el nombre lleva «IPHONE», si un
 * usado va a «Usados» o a «Celulares», si la ficha lleva marca— y se contesta
 * con lo que se toco, no con lo que dice el texto: si no se eligio ningun
 * modelo de iPhone, es otra marca.
 */
export function esIphoneElegido(modelo: string): boolean {
  if (partirModeloElegido(modelo).generacion !== "") return true;
  // Un iPhone anterior al 11 no tiene boton en el lineup: se carga en «Otra
  // marca» eligiendo la marca iPhone, y sigue siendo un iPhone.
  return MARCAS_DE_APPLE.includes(partirOtraMarca(modelo).marca);
}

/**
 * «Otra marca» viaja en el mismo texto que el modelo, con la marca adelante:
 * "SAMSUNG A57 5G". La marca es la primera palabra —el campo no deja escribir
 * espacios—, y el resto es el modelo.
 */
export function partirOtraMarca(modelo: string): { marca: string; resto: string } {
  const limpio = modelo.trim().toUpperCase().replace(/\s+/g, " ");
  const i = limpio.indexOf(" ");
  if (i < 0) return { marca: limpio, resto: "" };
  return { marca: limpio.slice(0, i), resto: limpio.slice(i + 1) };
}

/** Las marcas que dicen «iPhone»: el equipo es un iPhone aunque no sea del lineup. */
const MARCAS_DE_APPLE = ["IPHONE", "APPLE"];

/**
 * La marca para su columna, como la escriben las fichas que ya estan
 * («Samsung», «Xiaomi»). Un iPhone no la lleva: esa ficha ya se sabe de quien
 * es, y el alta de sellados nunca la puso.
 */
export function marcaDelEquipo(modelo: string): string | null {
  if (esIphoneElegido(modelo)) return null;
  const { marca } = partirOtraMarca(modelo);
  if (!marca) return null;
  return marca.charAt(0) + marca.slice(1).toLowerCase();
}

export interface DatosDelEquipo {
  /** Como lo devuelven los chips o el campo libre: "17 PRO MAX", "S22". */
  modelo: string;
  memoriaGb: number | null;
  esUsado: boolean;
}

/**
 * El nombre con el que el equipo queda guardado.
 *
 * "IPHONE 17 PRO MAX 256 GB" para un sellado, y el mismo más "(USADO)" para
 * una unidad usada. La consecuencia hay que decirla: dos unidades del mismo
 * modelo, la misma memoria y distinto color se llaman IGUAL. Lo que las
 * distingue —el color, la batería, el IMEI— está en sus columnas, la lista lo
 * muestra y el remito lo imprime. Ese es el punto: lo que distingue dos
 * equipos tiene que poder buscarse y filtrarse, y adentro de un nombre no se
 * puede.
 */
export function nombreDeEquipo({ modelo, memoriaGb, esUsado }: DatosDelEquipo): string {
  const limpio = modelo.trim().toUpperCase().replace(/\s+/g, " ");
  if (!limpio) return "";

  // ES iPHONE SI SE ELIGIO UN iPHONE, no si el texto lo parece. Un modelo del
  // lineup lleva «IPHONE» adelante; cualquier otra cosa llega de «Otra marca»
  // con su marca ya puesta y se guarda tal cual. Antes decidia una lista de
  // marcas conocidas, y un «PIXEL 8» que no estaba en la lista salia como
  // «IPHONE PIXEL 8 (USADO)» y caia en la solapa de los iPhone.
  const delLineup = partirModeloElegido(limpio).generacion !== "";
  // «APPLE XR» se guarda «IPHONE XR»: es como se lo busca, y la solapa de los
  // iPhone reconoce el nombre por ahí.
  const partes = [delLineup ? `IPHONE ${limpio}` : limpio.replace(/^APPLE\b/, "IPHONE")];
  const capacidad = etiquetaCapacidad(memoriaGb ?? 0);
  if (capacidad) partes.push(capacidad);
  if (esUsado) partes.push("(USADO)");
  return partes.join(" ");
}

/**
 * Ese modelo está en el lineup que conocemos.
 *
 * Es la pregunta que decide si el modelo se elige con chips o se escribe: los
 * iPhone que Apple hizo son una lista cerrada —la misma tabla que dice en qué
 * colores viene cada uno— y un Samsung no está en ninguna lista.
 */
export function esDelLineup(modelo: string): boolean {
  const clave = modelo.trim().toUpperCase().replace(/\s+/g, " ");
  return clave !== "" && clave in COLORES_POR_MODELO;
}

export interface ModeloElegido {
  /** "17", "AIR", o "" si el modelo no es de ninguna generación conocida. */
  generacion: string;
  /** "PRO MAX", o "" para el modelo base (y para una variante sin elegir). */
  variante: string;
  /** Lo que se escribió a mano. Solo tiene valor si no hay generación. */
  libre: string;
}

/**
 * De un modelo a las dos respuestas con las que se eligió.
 *
 * Es lo que deja abrir la ficha de un equipo YA CARGADO con los chips en la
 * posición correcta. Un modelo que el lineup no conoce no se fuerza a entrar:
 * vuelve como `libre`, el formulario lo muestra en el campo de texto y nadie
 * tiene que cambiarle el modelo a un Samsung para poder tocarle el precio.
 *
 * LO QUE DECIDE ES LA GENERACION, NO EL MODELO ENTERO
 * --------------------------------------------------
 * Se mira si la GENERACION existe, no si el modelo esta completo. Mirando el
 * modelo entero, elegir "18" —que no tiene modelo base: Apple hizo el 18 Pro y
 * el 18 Pro Max— daba un modelo que la tabla no conoce, y el selector lo tomaba
 * como si fuera un Samsung: saltaba a «Otro» con "18" escrito adentro, en el
 * medio de una eleccion que recien empezaba.
 *
 * Una eleccion a medias es una eleccion a medias, no otra marca. Que el modelo
 * este completo lo contesta `esModeloCompleto`, y es una validacion, no una
 * forma de mostrar los chips.
 */
export function partirModeloElegido(modelo: string): ModeloElegido {
  const limpio = modelo.trim().toUpperCase().replace(/\s+/g, " ");
  if (limpio === "") return { generacion: "", variante: "", libre: "" };
  const { generacion, variante } = partirModelo(limpio);
  if (!GENERACIONES.includes(generacion))
    return { generacion: "", variante: "", libre: modelo.trim() };
  return { generacion, variante, libre: "" };
}

/**
 * El modelo elegido es un telefono que existe.
 *
 * "18" no lo es: falta decir cual de los dos 18. "18 PRO" si. Lo que se escribio
 * a mano tampoco se valida —no hay lista de modelos Samsung— y por eso cuenta
 * como completo: la alternativa seria frenar una carga legitima.
 *
 * Sin esto, elegir la generacion y saltear la variante deja un "IPHONE 18
 * 256 GB" que no existe, con su precio y su stock.
 */
export function esModeloCompleto(modelo: string): boolean {
  return faltaDelModelo(modelo) === null;
}

/**
 * Qué falta para que el modelo sea uno de verdad. `null` = está completo.
 *
 * De otra marca hacen falta las dos cosas: la marca sola no dice qué teléfono
 * es, y el modelo solo no dice de quién. Y una marca que dice iPhone no es
 * otra marca: es un iPhone que se tiene que elegir arriba, porque escrito
 * no hay forma de saber cuál es ni en qué colores viene.
 */
export function faltaDelModelo(modelo: string): string | null {
  const { generacion } = partirModeloElegido(modelo);
  if (generacion === "") {
    if (modelo.trim() === "") return "¿Qué modelo es? (17 Pro, 16, 15 Plus… u otra marca)";
    const { marca, resto } = partirOtraMarca(modelo);
    if (!resto) return "Elegí la marca y escribí el modelo (ej: A57 5G).";
    // Un iPhone que tiene boton se elige arriba: escrito saldria sin los
    // colores de la tabla y como otra ficha del mismo telefono.
    if (MARCAS_DE_APPLE.includes(marca) && partirModeloElegido(resto).generacion !== "")
      return `El ${resto} está en los botones de arriba: elegilo ahí.`;
    return null;
  }
  if (esDelLineup(modelo)) return null;
  const opciones = variantesDe(generacion)
    .map((v) => (v === "" ? "el base" : v.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())))
    .join(", ");
  return `¿Cuál de los ${generacion}? Elegí la variante: ${opciones}.`;
}

/** Las dos respuestas, de vuelta a un modelo. Sin lineup gana lo escrito. */
export function armarModeloElegido(e: ModeloElegido): string {
  if (!e.generacion) return e.libre.trim().toUpperCase();
  return modeloDe(e.generacion, e.variante);
}

/**
 * Esta ficha es la de un TELEFONO, y no la de un cable.
 *
 * Es el interruptor que decide si el formulario pide modelo y memoria o pide
 * un nombre escrito. Se contesta con el nombre guardado, que es el único dato
 * que tienen todas las filas viejas: empieza con una marca de teléfono y dice
 * una capacidad. "CABLE IPHONE TIPO C A LIGHTNING" no entra —no tiene
 * capacidad— y "FUNDAS LEATHER CASE - IPHONE 15 AL 17 PRO MAX" tampoco, porque
 * no empieza con la marca.
 *
 * Una unidad usada entra siempre: no hay usados que no sean teléfonos.
 */
export function esEquipo(p: { nombre: string; es_usado?: boolean }): boolean {
  if (p.es_usado) return true;
  const nombre = p.nombre.trim().toUpperCase();
  if (!MARCAS.some((m) => nombre.startsWith(m))) return false;
  return parsearIphone(nombre).capacidadGb > 0;
}

/**
 * La ficha que se lee de un nombre guardado, para abrir el formulario.
 *
 * El modelo y la capacidad se sacan del nombre porque el modelo no tiene
 * columna: lo que se guarda es el nombre armado. La capacidad se prefiere de
 * la COLUMNA cuando está, que es el dato que alguien eligió en una lista
 * cerrada; el nombre es el respaldo para las fichas viejas, que se cargaron
 * antes de que la columna existiera.
 */
export function equipoDesdeProducto(p: {
  nombre: string;
  memoria_gb?: number | null;
  es_usado?: boolean;
}): ModeloElegido & { memoriaGb: number | null } {
  const sinSufijo = p.nombre.replace(/\s*\(USADO[^)]*\)\s*$/i, "").trim();
  const { modelo, capacidadGb } = parsearIphone(sinSufijo);
  // `parsearIphone` saca el "IPHONE" de adelante pero no el nombre de otra
  // marca: sin esto, un "SAMSUNG S22" volvería como modelo "SAMSUNG S22" y al
  // guardar quedaría "SAMSUNG SAMSUNG S22".
  // Las fichas viejas traen "A PEDIDO" o "STOCK" escritos adentro del nombre,
  // de cuando no habia campo donde ponerlo. Se sacan: eso ahora lo dice
  // `a_pedido`, y dejarlo pegado al modelo lo haria entrar como un modelo que
  // no existe.
  const sinOrigen = modelo
    .replace(/[-\s]*\bA\s+PEDIDO\b/i, "")
    .replace(/[-\s]*\bSTOCK\b/i, "")
    .replace(/\s*-\s*$/, "")
    .replace(/\s{2,}/g, " ")
    .trim();
  const desnudo = sinOrigen.replace(/^(SAMSUNG|XIAOMI|MOTOROLA|GALAXY|REDMI|POCO)\s+/i, "").trim();
  const marca = sinOrigen.slice(0, sinOrigen.length - desnudo.length).trim();
  const elegido = partirModeloElegido(desnudo);
  return {
    ...elegido,
    // La marca vuelve pegada a lo escrito: es parte del modelo de un Samsung, y
    // el armador la reconoce para no anteponerle IPHONE.
    libre: elegido.libre && marca ? `${marca} ${elegido.libre}` : elegido.libre,
    memoriaGb: p.memoria_gb ?? (capacidadGb > 0 ? capacidadGb : null),
  };
}
